import { createRoot, hydrateRoot } from "react-dom/client";
import { hydrate as hydrateQueryClient } from "@tanstack/react-query";
import App from "../App";
import { createQueryClient } from "../lib/queryClient";
import { installChunkReloadHandler } from "../lib/chunkReload";
import { installDomTranslationGuard } from "../lib/domTranslationGuard";
import { reportDomTranslationGuardHit } from "../lib/sentry";
import { i18nReady } from "../lib/i18n";
import "../index.css";

declare global {
  interface Window {
    __SSR__?: boolean;
    __REACT_QUERY_STATE__?: unknown;
  }
}

// A tab left open across a deploy can no longer fetch the old build's lazy
// chunks; reload it onto the new build instead of showing a crash screen.
installChunkReloadHandler();

// A page the browser translates would otherwise crash on React's next DOM
// update (lib/domTranslationGuard.ts). It must be in place before React's
// first render.
installDomTranslationGuard(reportDomTranslationGuardHit);

const queryClient = createQueryClient();

if (typeof window !== "undefined" && window.__REACT_QUERY_STATE__) {
  hydrateQueryClient(queryClient, window.__REACT_QUERY_STATE__);
}

const container = document.getElementById("root")!;

function render() {
  if (typeof window !== "undefined" && window.__SSR__) {
    hydrateRoot(container, <App queryClient={queryClient} />);
  } else {
    createRoot(container).render(<App queryClient={queryClient} />);
  }
}

// Locales load on demand (lib/i18n.ts). The first render must already speak
// the language the server rendered in, or hydration mismatches — so wait for
// it. Render even if it failed: fallback keys beat a blank page.
i18nReady.then(render, render);
